import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle, Circle, Loader2 } from "lucide-react";
import { apiService } from "@/lib/api-service";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface EmployeePaymentToggleProps {
  overtimeEntryId: string;
  employeeId: string;
  employeeName: string;
  paymentStatus: boolean;
  approvalStatus: string;
  projectId: string;
  disabled?: boolean;
}

export function EmployeePaymentToggle({
  overtimeEntryId,
  employeeId,
  employeeName,
  paymentStatus,
  approvalStatus,
  projectId,
  disabled = false,
}: EmployeePaymentToggleProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const queryClient = useQueryClient();

  const handleTogglePaymentStatus = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (isUpdating || disabled) return;

    setIsUpdating(true);
    try {
      // Update individual employee payment status
      await apiService.patch(
        `/overtime/${overtimeEntryId}/employee/${employeeId}/payment-status`,
        {
          paymentStatus: !paymentStatus,
        }
      );

      // Invalidate and refetch relevant queries
      await queryClient.invalidateQueries({
        queryKey: ["overtime", "project", projectId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["projects"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["project", projectId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["project-revision-history", projectId],
      });

      toast.success(
        `${employeeName} payment status ${!paymentStatus ? "marked as paid" : "marked as unpaid"}`
      );
    } catch (error) {
      console.error("Error updating employee payment status:", error);
      toast.error("Failed to update payment status");
    } finally {
      setIsUpdating(false);
    }
  };

  // Only show for approved overtime entries
  if (approvalStatus !== "APPROVED") {
    return null;
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleTogglePaymentStatus}
      disabled={disabled || isUpdating}
      className="h-auto p-1 hover:bg-muted"
      title={`Mark ${employeeName} as ${paymentStatus ? "unpaid" : "paid"}`}
    >
      {isUpdating ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : paymentStatus ? (
        <CheckCircle className="h-3 w-3 text-green-600" />
      ) : (
        <Circle className="h-3 w-3 text-muted-foreground" />
      )}
    </Button>
  );
}
